import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import isArray from 'lodash.isarray';
import {getAllActionTypes, getAllCategories} from 'src/components/change-log/utils';
import {FilterInput} from 'src/components/input';
import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ChangeLogDrawer} from 'src/modules/change-log';
import {NoResultPage} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {FilterStrip} from 'src/modules/profile/account/tabs/audit-log/filter/filter-strip';
import {getAccountLogById} from 'src/openapi/admin/apis/AccountLogApi';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';
import {pluralize} from '@pb/kit-formatters';

import {AuditLogTable} from './audit-log-table';
import {SECTION_NAME} from './constants';
import {LogFiltersBody} from './filter/log-filters-body';
import {useGetAccountAuditLog} from './hooks/useGetAccountAuditLog';
import {useGetAccountLogActionDictionary} from './hooks/useGetAccountLogActionDictionary';
import {accountAuditLogStateActions} from './slices/account-audit-log-state.slice';
import {accountAuditLogActions} from './slices/account-audit-log.slice';

import {
    accountAuditLogHeader,
    accountAuditLogLoadingContainer,
    accountAuditLogSectionTitle,
    accountAuditLogSeparator,
    accountAuditLogTotalCount,
} from './account-audit-log.module.styl';

import type {AccountAuditLogCriteria} from './typedef';
import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';

type Props = {
    accountId: number,
};

export const AccountAuditLog = ({accountId}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useDispatch<ApiDispatchForToolkit>();
    const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
    const {accountAuditLogSubmit} = useEventTracking();

    const {loadingError, result, cacheKeys} = useGetAccountAuditLog({accountId});
    const {
        data: actionTypeDictionary,
        loadingError: actionTypeDictionaryLoadingError,
        isLoading: actionTypeDictionaryIsLoading,
    } = useGetAccountLogActionDictionary({accountId});

    const noUserResultMessage = useMemo(() => {
        if (Object.keys(cacheKeys.filters).length && !result?.data.length) {
            return (
                <NoResultPage
                    title="No data found for the selected criteria."
                    description=""
                />
            );
        } else if (!Object.keys(cacheKeys.filters).length) {
            return (
                <NoResultPage
                    title="No data to show."
                    description=""
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    const changeSearchQuery = useCallback((value: string) => {
        const trimmed = value.trim();
        if (trimmed) {
            dispatch(accountAuditLogStateActions.setFilters({
                ...cacheKeys.filters,
                searchQuery: trimmed,
            }));
        } else {
            const {searchQuery, ...rest} = cacheKeys.filters;
            dispatch(accountAuditLogStateActions.setFilters(rest));
        }
    }, [cacheKeys.filters, dispatch]);

    const clearSearchQuery = useCallback(() => {
        const {searchQuery, ...otherFilters} = cacheKeys.filters;
        dispatch(accountAuditLogStateActions.setFilters(otherFilters));
    }, [cacheKeys.filters, dispatch]);

    const applyFilters = useCallback((filters: AccountAuditLogCriteria) => {
        dispatch(accountAuditLogStateActions.setFilters(filters));
        accountAuditLogSubmit({
            actionType: 'Apply Filters',
            filterCriteria: Object
                .entries(filters)
                .map(([key, value]) => `${key}: ${isArray(value) ? value.join(', ') : value}`),
        });
    }, [dispatch, accountAuditLogSubmit]);

    const clearFilters = useCallback(() => {
        const {searchQuery} = cacheKeys.filters;
        accountAuditLogSubmit({
            actionType: 'Reset',
            filterCriteria: Object
                .entries(cacheKeys.filters)
                .map(([key, value]) => `${key}: ${isArray(value) ? value.join(', ') : value}`),
        });

        const clearedFilters: AccountAuditLogCriteria = searchQuery ? {
            searchQuery: searchQuery as string,
        } : {};

        dispatch(accountAuditLogStateActions.setFilters(clearedFilters));
        return clearedFilters;
    }, [cacheKeys.filters, dispatch, accountAuditLogSubmit]);

    const closeDrawer = useCallback(() => {
        setSelectedLogId(null);
    }, []);

    const allActionTypes = useMemo(() => {
        if (actionTypeDictionaryIsLoading) {
            return [];
        }

        return getAllActionTypes(actionTypeDictionary);
    }, [actionTypeDictionary, actionTypeDictionaryIsLoading]);

    const allCategories = useMemo(() => {
        if (actionTypeDictionaryIsLoading) {
            return [];
        }

        return getAllCategories(actionTypeDictionary);
    }, [actionTypeDictionary, actionTypeDictionaryIsLoading]);


    const filtersCount = useMemo(() => {
        return Object.keys(cacheKeys.filters).filter(item => item !== 'searchQuery').length;
    }, [cacheKeys.filters]);

    const clearAllDisabled = useMemo(() => {
        return !Object.keys(cacheKeys.filters).length;
    }, [cacheKeys.filters]);

    useEffect(() => {
        return () => {
            dispatch(accountAuditLogActions.clearData());
            dispatch(accountAuditLogStateActions.clearData());
        };
    }, [dispatch]);

    return (
        <ServerResponseHandler
            serverError={loadingError || actionTypeDictionaryLoadingError}
            sectionName={SECTION_NAME}>
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <StickyElement id="manage-controls">
                    <div className={accountAuditLogHeader}>
                        <header className={accountAuditLogSectionTitle}>
                            <Group direction="h" alignItems="center">
                                <Typography
                                    fontWeight="semi-bold"
                                    variant="heading-level-3">
                                    Audit Log
                                </Typography>
                                {result?.totalCount ? (
                                    <>
                                        <span className={accountAuditLogSeparator}/>
                                        <span className={accountAuditLogTotalCount}>
                                            {pluralize(`${result.totalCount} Action[s]`, result.totalCount)}
                                        </span>
                                    </>
                                ) : null}
                            </Group>
                        </header>
                        {!(!result?.totalCount && !Object.keys(cacheKeys.filters).length) ? (
                            <Group direction="v" gap="m" as="section">
                                <Group direction="h" gap="m" as="section">
                                    <FilterInput
                                        id="account-audit-log-filter"
                                        placeholder="Search for actions by description"
                                        onChange={changeSearchQuery}
                                        onClear={clearSearchQuery}
                                        timeOut={1000}
                                        width={600}
                                        value={(cacheKeys.filters?.searchQuery || '') as string}
                                    />
                                    <SectionFilter<AccountAuditLogCriteria>
                                        onApply={applyFilters}
                                        maxHeight={500}
                                        width={465}
                                        onClear={clearFilters}
                                        filters={cacheKeys.filters as AccountAuditLogCriteria}
                                        getAnchor={({anchorRef, positionRef, getAnchorProps}) => (
                                            <Button
                                                variant="Type.SECONDARY"
                                                startIcon={(
                                                    <Icon
                                                        type={filtersCount ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY}
                                                    />
                                                )}
                                                endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                                ref={anchorRef}
                                                {...getAnchorProps()}>
                                                <b>Filter</b>
                                            </Button>
                                        )}
                                        defaultFilters={{}}>
                                        {(filter, onChange) => (
                                            <LogFiltersBody
                                                filter={filter}
                                                onChange={onChange}
                                                actionTypeDictionary={actionTypeDictionary}
                                            />
                                        )}
                                    </SectionFilter>
                                </Group>
                                <FilterStrip
                                    clearAllDisabled={clearAllDisabled}
                                    actionTypeDictionary={allActionTypes}
                                    categoryDictionary={allCategories}
                                />
                            </Group>
                        ) : null}
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    {result?.data.length ? (
                        <div className={accountAuditLogLoadingContainer}>
                            <AuditLogTable
                                onLogClick={setSelectedLogId}
                                rows={result?.data || []}
                                totalCount={result?.totalCount || 0}
                                pagination={cacheKeys.pagination}
                                actionTypeDictionary={allActionTypes}
                            />
                        </div>
                    ) : noUserResultMessage}
                </BoxLoading>
                <ChangeLogDrawer
                    selectedLogId={selectedLogId}
                    onClose={closeDrawer}
                    loader={(logId: string) => apiDispatch(getAccountLogById({logId}))}
                />
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
